import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import apiClient from '../services/api';
import { PageHeader, PageDescription } from '../main/LayoutStyles';
import { Card, AlertBox, StyledButton } from '../main/CommonStyles';
import { Loader2, AlertTriangle, Save, GripVertical, Star } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const ManagementWrapper = styled.div`
    max-width: 800px;
    margin: 0;
    padding: 0;
`;

const GlobalControls = styled(Card)`
    padding: 1rem;
    margin-bottom: 1.5rem;
    label {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
    }
`;

const ActionList = styled.div`
    margin-top: 1rem;
`;

const ActionItem = styled(Card)`
    margin-bottom: 1rem;
    padding: 0.75rem;
    display: flex;
    gap: 0.75rem;
    align-items: center;
    background-color: ${({ theme, $isDraggable }) => !$isDraggable && theme.colors.neutralLight};
    opacity: ${({ $isDraggable }) => !$isDraggable && 0.6};
`;

const ItemDetails = styled.div`
    flex-grow: 1;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 150px;

    .icon-preview {
        padding: 0.5rem;
        background-color: ${({ theme }) => theme.colors.primaryLightest};
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        color: ${({ theme }) => theme.colors.primaryDark};
    }
`;

const FormField = styled.div`
    flex-grow: 1;

    input {
        width: 80%;
        padding: 0.5rem;
        border: 1px solid #ccc;
        border-radius: 8px;
        font-size: 0.9rem;
    }
`;

const ItemControls = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;

    .star-button {
        background: none; border: none; cursor: pointer; padding: 0.25rem;
        color: ${({ theme, $isCentral }) => $isCentral ? '#f59e0b' : theme.colors.textDisabled};

        &:hover { opacity: 0.7; }
    }
`;

const DragHandle = styled.div`
    cursor: grab;
    color: ${({ theme }) => theme.colors.textDisabled};
    opacity: ${({ $isDraggable }) => $isDraggable ? 1 : 0.4};
    display: flex;
    align-items: center;
    padding: 0 0.25rem;
`;

const ToggleSwitch = styled.label`
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
    cursor: pointer;

    input { opacity: 0; width: 0; height: 0; }
    .slider {
        position: absolute; top: 0; left: 0; right: 0; bottom: 0;
        background-color: #ccc; transition: .4s; border-radius: 22px;
    }
    .slider:before {
        position: absolute; content: "";
        height: 16px; width: 16px; left: 3px; bottom: 3px;
        background-color: white; transition: .4s; border-radius: 50%;
    }
    input:checked + .slider { background-color: ${({ theme }) => theme.colors.primary}; }
    input:checked + .slider:before { transform: translateX(18px); }
`;

const BottomNavManagement = () => {
    const [config, setConfig] = useState([]);
    const [masterShowLabels, setMasterShowLabels] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/features/system-admin/bottom-nav-config');
            const sortedConfig = response.data.sort((a, b) => a.displayOrder - b.displayOrder);
            setConfig(sortedConfig);
            const firstEnabled = sortedConfig.find(item => item.enabled && !item.isCentral);
            setMasterShowLabels(firstEnabled ? firstEnabled.showLabel : true);
        } catch (err) {
            setError('Nie udało się załadować konfiguracji.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const activeItems = config.filter(item => item.enabled).sort((a,b) => a.displayOrder - b.displayOrder);

    const reorderAndSync = (currentConfig) => {
        const active = currentConfig.filter(i => i.enabled).sort((a, b) => a.displayOrder - b.displayOrder);
        const inactive = currentConfig.filter(i => !i.enabled);
        return [
            ...active.map((item, index) => ({...item, displayOrder: index + 1})),
            ...inactive.map((item, index) => ({...item, displayOrder: active.length + index + 1}))
        ].sort((a,b) => a.id - b.id);
    };

    const handleToggleMasterShowLabels = (e) => {
        const isChecked = e.target.checked;
        setMasterShowLabels(isChecked);
        setConfig(prevConfig =>
            prevConfig.map(item => ({ ...item, showLabel: isChecked }))
        );
    };

    const handleToggleEnable = (id) => {
        if (activeItems.length >= 5 && !config.find(item => item.id === id)?.enabled) {
            alert("Można włączyć maksymalnie 5 akcji.");
            return;
        }
        setConfig(prev => reorderAndSync(prev.map(item => item.id === id ? { ...item, enabled: !item.enabled, isCentral: false } : item)));
    };

    const handleUpdateLabel = (id, newLabel) => {
        setConfig(prev => prev.map(item => item.id === id ? { ...item, label: newLabel } : item));
    };

    const handleToggleCentral = (id) => {
        setConfig(prev => reorderAndSync(prev.map(item => ({ ...item, isCentral: item.id === id ? !item.isCentral : false }))));
    };

    const handleOnDragEnd = (result) => {
        if (!result.destination) return;
        let items = Array.from(activeItems);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        const activeItemsMap = new Map(items.map((item, index) => [item.id, index + 1]));
        const newConfig = config.map(item => activeItemsMap.has(item.id) ? { ...item, displayOrder: activeItemsMap.get(item.id) } : item);
        setConfig(reorderAndSync(newConfig));
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            await apiClient.put('/features/system-admin/bottom-nav-config', config);
            alert('Konfiguracja zapisana pomyślnie!');
            fetchData();
        } catch (err) {
            setError('Błąd podczas zapisu konfiguracji.');
        } finally {
            setSaving(false);
        }
    };

    const renderIconPreview = (iconName) => {
        const Icon = LucideIcons[iconName];
        return Icon ? <Icon size={20} /> : <LucideIcons.HelpCircle size={20} />;
    };

    if (loading) return <Loader2 className="icon-spin" />;

    return (
        <ManagementWrapper>
            <PageHeader>Zarządzanie Dolnym Paskiem Nawigacyjnym</PageHeader>
            <PageDescription>Wybierz do 5 akcji, przeciągnij je, aby ustawić kolejność, i dostosuj ich wygląd.</PageDescription>
            {error && <AlertBox type="error">{error}</AlertBox>}

            <GlobalControls>
                <label>
                    <ToggleSwitch>
                        <input type="checkbox" checked={masterShowLabels} onChange={handleToggleMasterShowLabels} />
                        <span className="slider"></span>
                    </ToggleSwitch>
                    Wyświetlaj etykiety pod ikonami
                </label>
            </GlobalControls>

            <Card>
                <h3 style={{marginBottom: '1rem'}}>Aktywne Akcje ({activeItems.length}/5)</h3>
                <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="active-actions">
                        {(provided) => (
                            <ActionList {...provided.droppableProps} ref={provided.innerRef}>
                                {activeItems.map((item, index) => (
                                    <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                                        {(providedHandle) => (
                                            <div ref={providedHandle.innerRef} {...providedHandle.draggableProps} {...providedHandle.dragHandleProps}>
                                                <ActionItem $isEnabled={true} $isDraggable={true}>
                                                    <ItemDetails>
                                                        <div className="icon-preview">{renderIconPreview(item.iconName)}</div>
                                                        <FormField>
                                                            <input value={item.label} onChange={e => handleUpdateLabel(item.id, e.target.value)} placeholder="Etykieta" />
                                                        </FormField>
                                                    </ItemDetails>
                                                    <ItemControls $isCentral={item.isCentral}>
                                                        {index === 2 && activeItems.length === 5 && (
                                                            <button className="star-button" onClick={() => handleToggleCentral(item.id)} title="Oznacz jako powiększony, centralny przycisk">
                                                                <Star size={22} fill={item.isCentral ? '#f59e0b' : 'none'}/>
                                                            </button>
                                                        )}
                                                        <ToggleSwitch title="Włącz/Wyłącz">
                                                            <input type="checkbox" checked={item.enabled} onChange={() => handleToggleEnable(item.id)} />
                                                            <span className="slider"></span>
                                                        </ToggleSwitch>
                                                        <DragHandle title="Zmień kolejność"><GripVertical /></DragHandle>
                                                    </ItemControls>
                                                </ActionItem>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </ActionList>
                        )}
                    </Droppable>
                </DragDropContext>
            </Card>

            <Card style={{marginTop: '2rem'}}>
                <h3 style={{marginBottom: '1rem'}}>Dostępne Akcje (nieaktywne)</h3>
                <ActionList>
                    {config.filter(item => !item.enabled).map((item) => (
                        <ActionItem key={item.id} $isEnabled={false} $isDraggable={false}>
                            <ItemDetails>
                                <div className="icon-preview">{renderIconPreview(item.iconName)}</div>
                                <FormField>
                                    <input value={item.label} onChange={e => handleUpdateLabel(item.id, e.target.value)} placeholder="Etykieta" />
                                </FormField>
                            </ItemDetails>
                            <ItemControls>
                                <ToggleSwitch title="Włącz/Wyłącz">
                                    <input type="checkbox" checked={item.enabled} onChange={() => handleToggleEnable(item.id)} />
                                    <span className="slider"></span>
                                </ToggleSwitch>
                            </ItemControls>
                        </ActionItem>
                    ))}
                </ActionList>
            </Card>

            <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '2rem'}}>
                <StyledButton onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="icon-spin"/> : <Save/>}
                    Zapisz Konfigurację
                </StyledButton>
            </div>
        </ManagementWrapper>
    );
};

export default BottomNavManagement;